import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Permissions, ProxyServiceConfigurationDc, RemoteTileServiceConfigurationDc } from "@evergis/api";
import { AsyncState } from "@evergis/react-fetching/es/hooks/useAsyncState";

import { PUBLIC_ROLE } from "../../../../hooks/acl/publicAccess";
import { createProxyLayer, createRemoteTileLayer } from "../../../../resources/layers";
import { ResourceMeta } from "../../../../hooks/useResourceMeta";
import { getResourceName } from "../../../../utils/resourceName";
import { ProxyServiceType } from "../../AddProxyService/components/types";
import { TmsType } from "../types";

type ProxyServiceInput = {
  meta: ResourceMeta;
  sourceType: TmsType | ProxyServiceType;
  sourceUrl: string;
  layers: string[];
  subDomains?: string[];
};

type ProxyServiceOutput = {
  createTms: () => Promise<void>;
  tmsResponse: AsyncState<ProxyServiceConfigurationDc | Partial<RemoteTileServiceConfigurationDc>, unknown>;
  tmsError: Error;
};

// eslint-disable-next-line max-lines-per-function
export const useCreateTms = ({
  meta,
  sourceType,
  sourceUrl,
  layers,
  subDomains,
}: ProxyServiceInput): ProxyServiceOutput => {
  const { t } = useTranslation("dialogs");
  const [error, setError] = useState<Error>();
  const [proxyMutation, proxyResponse] = createProxyLayer.use();
  const [remoteMutation, remoteResponse] = createRemoteTileLayer.use();

  const handleCreate = useCallback(async () => {
    setError(null);

    if (sourceType === TmsType.TMS) {
      const urlDot = sourceUrl.split(".");
      const urlSlash = !!urlDot[0] && urlDot[0].split("/");
      const subDomain = urlSlash.length > 1 && urlSlash.pop();

      if (!subDomain) {
        setError({ name: "urlError", message: t("addTmsService.tmsUrlError") });

        return;
      }

      await remoteMutation({
        input: {
          name: meta.name || getResourceName(),
          alias: meta.alias,
          description: meta.description,
          urlFormat: sourceUrl,
          subDomains,
          allowDirectAccess: false,
          RequestTimeout: 0,
          tileCacheLimit: 50000,
          acl: {
            data: [
              {
                role: PUBLIC_ROLE,
                permissions: Permissions.Read,
              },
            ],
          },
        },
      });
    } else {
      await proxyMutation({
        input: {
          name: meta.name || getResourceName(),
          alias: meta.alias,
          description: meta.description,
          sourceType,
          sourceUrl,
          layers,
        },
      });
    }
  }, [
    sourceType,
    sourceUrl,
    t,
    remoteMutation,
    meta.alias,
    meta.description,
    meta.name,
    proxyMutation,
    layers,
    subDomains,
  ]);

  return {
    createTms: handleCreate,
    tmsError: error,
    tmsResponse:
      sourceType === TmsType.TMS
        ? (remoteResponse as AsyncState<Partial<RemoteTileServiceConfigurationDc>, unknown>)
        : (proxyResponse as AsyncState<ProxyServiceConfigurationDc, unknown>),
  };
};
